import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  ArrowLeftIcon,
  BeakerIcon,
  CalculatorIcon,
  CheckCircleIcon,
  CheckIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilSquareIcon,
  PrinterIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { isBlankHtml, RichText } from "../../../components/KaTeXComponent/RichText";
import HelpButton from "../../../components/Help/HelpButton";
import TestsGuide from "../../../components/Help/guides/TestsGuide";
import OverflowMenu from "../../../components/ui/OverflowMenu";
import ContextQuestionDialog from "../../../components/Passages/ContextQuestionDialog";
import usePassagesAvailable from "../../../components/Passages/usePassagesAvailable";
import PassageDialog from "../../../components/Passages/PassageDialog";
import usePassage from "../../../components/Passages/usePassage";
import { getPassage } from "../../../services/passageService";
import { getApiErrorCode, getApiErrorMessage } from "../../../utils/apiError";
import useTestBuilder from "./useTestBuilder";
import QuestionBrowser from "./QuestionBrowser";
import QuestionPreview from "./QuestionPreview";
import TestComposition, { SaveStatus } from "./TestComposition";
import TestPreview from "./TestPreview";
import { BlockedRemoveDialog, EditTestDialog, InfoDialog, LeaveDialog } from "./TestDialogs";
import {
  backLinkClass,
  cardClass,
  focusRing,
  iconButtonClass,
  isDraft,
  isTestBlocked,
  linkButtonClass,
  quietButtonClass,
  Spinner,
  useMediaQuery,
} from "./ui";

// The header's one main button: Done (it fills its row on phones, beside "⋯").
const doneButtonClass = `inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-blue-600 px-5 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 hover:bg-blue-700 max-md:flex-1 md:h-10 ${focusRing}`;
// Question codes for a message: the first five, then how many more.
const listCodes = (codes) => (codes.length > 5 ? `${codes.slice(0, 5).join(", ")} +${codes.length - 5}` : codes.join(", "));

// The text of a passage, read-only (the "View passage" dialog of a block).
function PassageText({ uuid }) {
  const [t] = useTranslation();
  const { data, error, retry } = usePassage(uuid);
  if (error) {
    return (
      <div role="alert" className="flex flex-wrap items-center gap-2 text-red-700">
        <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
        {t("passages.panel.load_failed")}
        <button type="button" onClick={retry} className={`${linkButtonClass} max-sm:min-h-11`}>
          {t("passages.panel.retry")}
        </button>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="space-y-2" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <span key={i} className="block h-4 rounded bg-slate-100 motion-safe:animate-pulse" />
        ))}
      </div>
    );
  }
  if (isBlankHtml(data.body)) return <p className="text-slate-500">{t("passages.panel.empty")}</p>;
  return (
    <div className="passage-content">
      <RichText html={data.body} dollarMath={false} />
    </div>
  );
}

function UndoToast({ text, onUndo, closeToast }) {
  const [t] = useTranslation();
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-slate-800">{text}</span>
      <button
        type="button"
        onClick={() => {
          onUndo();
          closeToast();
        }}
        className={`${linkButtonClass} shrink-0 max-sm:min-h-11`}
      >
        {t("test_bank.builder.undo")}
      </button>
    </div>
  );
}

/**
 * Test builder (/test/:uuid): the question bank on one side and the test on the other. Every change
 * is saved right away. location.state: {from} is where Back and Done go (the test list or the exam),
 * {justCreated} shows a welcome banner after the create form. ?edit=heading (the exam's Word asks for
 * the test's page heading) opens Edit test on that field.
 */
export default function TestBuilder() {
  const [t] = useTranslation();
  const { uuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = location.state?.from || "/test_list";
  const backLabel = backTo.startsWith("/exams_list") ? t("test_bank.common.back_to_exam") : t("test_bank.single_create.back_to_list");
  const builder = useTestBuilder(uuid);
  const { test, items, totals, inTest, pendingIds, addQuestions, removeItem: removeTestItem, restoreItem, moveItem, setPoints: setItemPoints, flush } = builder;
  const { movePassage: moveTestPassage, gatherPassage, removePassage: removeTestPassage, restorePassage, syncPassage } = builder;
  const wide = useMediaQuery("(min-width: 1920px)");
  // Phones: the header keeps Done and puts Preview and Edit test in "⋯".
  const roomyHeader = useMediaQuery("(min-width: 768px)");
  const [justCreated, setJustCreated] = useState(!!location.state?.justCreated);
  // Read by stable callbacks (the memoized bank rows get them as props).
  const itemsRef = useRef(items);
  const totalsRef = useRef(totals);
  itemsRef.current = items;
  totalsRef.current = totals;
  const [pane, setPane] = useState("bank");
  const [defaultPoints, setDefaultPoints] = useState(1);
  const [preview, setPreview] = useState({ question: null, list: [], open: false });
  // The preview is a sheet (a dialog) below 1920px: while it is open, nothing else on the page can be used.
  const sheetOpenRef = useRef(false);
  sheetOpenRef.current = !wide && preview.open;
  const [selection, setSelection] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  // The field Edit test opens on: "heading", or null for its first one.
  const [editField, setEditField] = useState(null);
  const openEdit = (field = null) => {
    setEditField(field);
    setEditOpen(true);
  };
  // ?edit=… asked for Edit test: it opens once the test is loaded.
  const editParam = new URLSearchParams(location.search).get("edit");
  const [editRequest, setEditRequest] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  // The information's Edit opens Edit test once the information has closed (its focus trap holds the focus until then).
  const editAfterInfo = useRef(false);
  const [contextOpen, setContextOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  // "+ Context question" only once the server has passages (an old backend would fail the dialog).
  const passagesAvailable = usePassagesAvailable();
  // The passage stays set while its dialog closes, so the dialog does not change during the transition.
  const [passageView, setPassageView] = useState({ open: false, passage: null });
  const [passageEdit, setPassageEdit] = useState({ open: false, uuid: null });
  const [leave, setLeave] = useState({ open: false, adding: false });
  // Questions the server refused as not approved (400 not_approved), each with the bank's load (`bankKey`) that
  // the refusal asked for: the copies here were older than their exam's rule or their status, so they show as
  // blocked, until a bank page loaded since then shows one can be added now (approved, or the rule turned off).
  const [blockedIds, setBlockedIds] = useState(() => new Map());
  // A new value loads the bank's page again (after such a refusal its rows are out of date).
  const [bankKey, setBankKey] = useState(0);
  const bankKeyRef = useRef(0);
  // Rows whose questions could not be added back (isTestBlocked) go only once this dialog has asked. `inSheet`:
  // it was asked from the preview sheet, and opens inside it.
  const [blockedRemoval, setBlockedRemoval] = useState({ open: false, rows: [], passage: null, inSheet: false });
  // The question it is asking: {resolve, agreed, trigger} (trigger: what had the focus when it opened).
  const decisionRef = useRef(null);
  const [flashIds, setFlashIds] = useState(() => new Set());
  const [announcement, setAnnouncement] = useState("");
  const flashTimer = useRef(null);
  const headingRef = useRef(null);
  const mountedRef = useRef(true);
  // Undo toasts of this builder: they go with it (an Undo after leaving would still write to the test).
  const undoToastIds = useRef(new Set());

  useEffect(() => () => clearTimeout(flashTimer.current), []);
  useEffect(() => {
    mountedRef.current = true;
    const undoIds = undoToastIds.current;
    return () => {
      mountedRef.current = false;
      undoIds.forEach((id) => toast.dismiss(id));
      // A removal the dialog was asking about does not happen.
      decisionRef.current?.resolve(false);
      decisionRef.current = null;
    };
  }, []);
  // history.state survives a reload and Back: the banner is shown once, then the flag is forgotten. ?edit=… goes the
  // same way (replace: no new history entry), so a reload or Back does not open Edit test again.
  useEffect(() => {
    if (!location.state?.justCreated && !editParam) return;
    if (editParam) setEditRequest(editParam);
    const params = new URLSearchParams(location.search);
    params.delete("edit");
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true, state: { from: backTo } });
  }, [editParam]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (test) headingRef.current?.focus({ preventScroll: true });
  }, [test?.uuid]); // eslint-disable-line react-hooks/exhaustive-deps
  // After the title's focus above: closed, the dialog gives the focus back to the title.
  useEffect(() => {
    if (!test || !editRequest) return;
    openEdit(editRequest === "heading" ? "heading" : null);
    setEditRequest(null);
  }, [test, editRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  // Closing the tab with unsaved writes or an unadded selection asks first.
  const unsaved = builder.busy || selection.length > 0;
  useEffect(() => {
    if (!unsaved) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsaved]);

  const flash = (questionUuids) => {
    setFlashIds(new Set(questionUuids));
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashIds(new Set()), 1600);
  };

  // Counts failed adds, so leaving can tell that one failed while it waited for the writes.
  const failedAddsRef = useRef(0);

  // A failed add of `questions` says why. A 400 not_approved names the questions their exam does not take
  // into tests yet (`questions` in the answer; nothing was added): they are marked so they can't be picked
  // again, and the bank's page is loaded anew. A 400 already_in_test is no failure: the question is in the test
  // (added elsewhere meanwhile), which is loaded again and shows it once.
  const addError = useCallback(
    (err, questions = []) => {
      const code = getApiErrorCode(err);
      if (code === "already_in_test") {
        toast.info(getApiErrorMessage(t, err, "test_bank.builder.add_failed"));
        return;
      }
      if (code !== "not_approved") {
        toast.error(getApiErrorMessage(t, err, "test_bank.builder.add_failed"));
        return;
      }
      const refused = new Set([].concat(err?.response?.data?.questions || []).map(String));
      bankKeyRef.current += 1;
      const key = bankKeyRef.current;
      setBlockedIds((prev) => {
        const next = new Map(prev);
        refused.forEach((id) => next.set(id, key));
        return next;
      });
      setBankKey(key);
      const codes = questions.filter((q) => refused.has(String(q?.uuid))).map((q) => q.code || "").filter(Boolean);
      let message = codes.length
        ? t("test_bank.builder.not_approved_refused", { count: codes.length, codes: listCodes(codes) })
        : getApiErrorMessage(t, err, "test_bank.builder.add_failed");
      // The others of the batch were left out too (all or nothing), apart from those in the test already.
      const saved = new Set(itemsRef.current.filter((it) => !it.pending).map((it) => it.question?.uuid));
      if (questions.some((q) => q?.uuid && !refused.has(String(q.uuid)) && !saved.has(q.uuid))) {
        message = `${message} ${t("test_bank.builder.not_approved_rest")}`;
      }
      toast.error(message);
    },
    [t]
  );

  // A bank page (`rows`) asked for with `key`: loaded after the refusal of a question, it shows the question as it
  // is now. One that can be added since (approved, or its exam's rule turned off) is no longer held back, in any of
  // its copies: the server takes it.
  const bankLoaded = useCallback((rows, key) => {
    setBlockedIds((prev) => {
      const lifted = rows.filter((q) => key >= prev.get(q?.uuid) && !isTestBlocked(q));
      if (!lifted.length) return prev;
      const next = new Map(prev);
      lifted.forEach((q) => next.delete(q.uuid));
      return next;
    });
  }, []);

  // Resolves true when the questions were added (or were all in the test already).
  const add = useCallback(
    async (questions) => {
      try {
        const created = await addQuestions(questions, defaultPoints);
        if (!created.length) return true;
        flash(created.map((tq) => tq.question?.uuid ?? tq.question));
        const message = t("test_bank.editor.questions_added", { count: created.length });
        setAnnouncement(message);
        if (created.length > 1) toast.success(message);
        return true;
      } catch (err) {
        failedAddsRef.current += 1;
        addError(err, questions);
        return false;
      }
    },
    [addError, addQuestions, defaultPoints, t]
  );

  // Resolves, once the dialog has closed, true if the user agreed to remove `rows` (with `passage`, the whole
  // passage goes), false if they keep them. A question asked about before is kept.
  const askBlockedRemoval = useCallback(
    (rows, passage = null) =>
      new Promise((resolve) => {
        decisionRef.current?.resolve(false);
        decisionRef.current = { resolve, agreed: false, trigger: document.activeElement };
        setBlockedRemoval({ open: true, rows, passage, inSheet: sheetOpenRef.current });
      }),
    []
  );
  const answerBlockedRemoval = (agreed) => {
    if (decisionRef.current) decisionRef.current.agreed = agreed;
    setBlockedRemoval((dialog) => ({ ...dialog, open: false }));
  };
  // The dialog is gone: what asked goes on (the list puts the focus on its row), and a focus left nowhere goes
  // back to what opened it (the bank's or the preview's button, still on the page).
  const blockedRemovalClosed = () => {
    const decision = decisionRef.current;
    decisionRef.current = null;
    if (!decision) return;
    decision.resolve(decision.agreed);
    requestAnimationFrame(() => {
      const lost = !document.activeElement || document.activeElement === document.body;
      if (lost && decision.trigger?.isConnected) decision.trigger.focus({ preventScroll: true });
    });
  };

  // Resolves true when the row is being removed. Removed, it can be put back with Undo — unless its question
  // could not be added back (isTestBlocked): then the user is asked first, and there is no Undo.
  const removeItem = useCallback(
    async (item) => {
      if (!item || item.pending) return false;
      const final = isTestBlocked(item.question);
      if (final && !(await askBlockedRemoval([item]))) return false;
      // While the dialog asked, the row may have gone or been reloaded.
      const row = itemsRef.current.find((it) => it.uuid === item.uuid);
      if (!mountedRef.current || !row || row.pending) return false;
      const index = itemsRef.current.indexOf(row);
      const code = row.question?.code || "";
      let removal;
      const remove = () => {
        removal = removeTestItem(row);
      };
      // After the dialog this runs outside a click: the list is drawn at once, so the focus finds the next row.
      if (final) flushSync(remove);
      else remove();
      removal.catch((err) => toast.error(getApiErrorMessage(t, err, "test_bank.builder.remove_failed")));
      const text = t("test_bank.builder.removed_toast", { code });
      setAnnouncement(text);
      if (final) {
        toast(text);
        return true;
      }
      const toastId = toast(
        ({ closeToast }) => (
          <UndoToast
            text={text}
            closeToast={closeToast}
            onUndo={() => {
              // Still clickable while it fades out after the builder is gone.
              if (!mountedRef.current) return;
              restoreItem({ question: row.question, points: row.points, index }).catch((err) => addError(err, [row.question]));
            }}
          />
        ),
        { autoClose: 6000, closeOnClick: false, onClose: () => undoToastIds.current.delete(toastId) }
      );
      undoToastIds.current.add(toastId);
      return true;
    },
    [addError, askBlockedRemoval, removeTestItem, restoreItem, t]
  );

  const removeQuestion = useCallback(
    (question) => removeItem(itemsRef.current.find((it) => it.question?.uuid === question?.uuid)),
    [removeItem]
  );

  // A passage block with all its questions, with Undo like a single row; as there, rows that could not be added
  // back are asked about first, and then there is no Undo.
  const removePassage = useCallback(
    async (passage) => {
      const rowsOf = () => itemsRef.current.filter((it) => it.question?.passage?.uuid === passage?.uuid);
      const before = rowsOf();
      if (!before.length || before.some((it) => it.pending)) return false;
      const final = before.filter((it) => isTestBlocked(it.question));
      if (final.length && !(await askBlockedRemoval(final, passage))) return false;
      const rows = rowsOf();
      if (!mountedRef.current || !rows.length || rows.some((it) => it.pending)) return false;
      let removal;
      const remove = () => {
        removal = removeTestPassage(passage.uuid);
      };
      // After the dialog: drawn at once, as for a row.
      if (final.length) flushSync(remove);
      else remove();
      removal.catch((err) => toast.error(getApiErrorMessage(t, err, "test_bank.builder.passage.remove_failed")));
      const text = t("test_bank.builder.passage.removed_toast", { count: rows.length });
      setAnnouncement(text);
      if (final.length) {
        toast(text);
        return true;
      }
      const toastId = toast(
        ({ closeToast }) => (
          <UndoToast
            text={text}
            closeToast={closeToast}
            onUndo={() => {
              // Still clickable while it fades out after the builder is gone.
              if (!mountedRef.current) return;
              // Put back once the removal is saved; a failed removal brings the rows back with the reloaded list.
              removal.then(
                (removed) => removed && restorePassage(removed).catch((err) => addError(err, removed.rows.map((r) => r.question))),
                () => null
              );
            }}
          />
        ),
        { autoClose: 6000, closeOnClick: false, onClose: () => undoToastIds.current.delete(toastId) }
      );
      undoToastIds.current.add(toastId);
      return true;
    },
    [addError, askBlockedRemoval, removeTestPassage, restorePassage, t]
  );

  // The context question dialog has linked the questions to the passage: those in the test join its block,
  // the others are added next to them with their difficulty points or the default ones (not its authors'
  // unfinished drafts, nor those their exam does not take into tests yet: they stay linked, out of the test).
  const addPassage = useCallback(
    async ({ passage, questions }) => {
      if (!passage?.uuid || !questions?.length) return;
      const linked = questions.map((q) => (q.passage?.uuid ? q : { ...q, passage }));
      syncPassage({ uuid: passage.uuid, name: passage.name, questions: linked });
      // A passage's questions live in its folder. Those taken from the passage itself carry none: its folder
      // (and with it its exam's rule) comes from the passage; without it the server still refuses them.
      let folder = null;
      if (linked.some((q) => !q.folder)) {
        folder = await getPassage(passage.uuid).then(
          (detail) => detail?.folder || null,
          () => null
        );
      }
      if (!mountedRef.current) return;
      const present = new Set(itemsRef.current.map((it) => it.question?.uuid));
      const held = linked.filter(
        (q) => !isDraft(q) && !present.has(q.uuid) && (isTestBlocked(q, q.folder || folder) || blockedIds.has(q.uuid))
      );
      if (held.length) {
        toast.warning(t("test_bank.builder.passage.blocked_skipped", { codes: listCodes(held.map((q) => q.code || "").filter(Boolean)) }));
      }
      const list = linked.filter((q) => !isDraft(q) && !held.includes(q));
      if (!list.length) return;
      let created;
      try {
        created = await addQuestions(list, defaultPoints);
      } catch (err) {
        failedAddsRef.current += 1;
        addError(err, list);
        return;
      }
      if (created.length) flash(created.map((tq) => tq.question?.uuid ?? tq.question));
      // All of them were in the test already, maybe apart from each other.
      else gatherPassage(passage.uuid).catch((err) => toast.error(getApiErrorMessage(t, err, "test_bank.builder.passage.move_failed")));
      const message = t("passages.context.added", { count: list.length });
      setAnnouncement(message);
      toast.success(message);
    },
    [addError, addQuestions, blockedIds, defaultPoints, gatherPassage, syncPassage, t]
  );

  const move = useCallback(
    (itemUuid, toIndex) => {
      moveItem(itemUuid, toIndex).catch((err) => toast.error(getApiErrorMessage(t, err, "test_bank.builder.move_failed")));
    },
    [moveItem, t]
  );

  const movePassage = useCallback(
    (passageUuid, k) => {
      moveTestPassage(passageUuid, k).catch((err) => toast.error(getApiErrorMessage(t, err, "test_bank.builder.passage.move_failed")));
    },
    [moveTestPassage, t]
  );
  const viewPassage = useCallback((passage) => setPassageView({ open: true, passage }), []);
  const editPassage = useCallback((passage) => setPassageEdit({ open: true, uuid: passage.uuid }), []);

  const setPoints = useCallback(
    (itemUuid, points) => {
      setItemPoints(itemUuid, points).catch((err) => toast.error(getApiErrorMessage(t, err, "test_bank.builder.points_failed")));
    },
    [setItemPoints, t]
  );

  const openFromBank = useCallback((question, list) => setPreview({ question, list, open: true }), []);
  const openFromTest = useCallback(
    (item) => setPreview({ question: item.question, list: items.map((it) => it.question), open: true }),
    [items]
  );
  const previewIndex = preview.question ? preview.list.findIndex((q) => q?.uuid === preview.question.uuid) : -1;
  const stepPreview = (delta) => {
    const next = preview.list[previewIndex + delta];
    if (next) setPreview((p) => ({ ...p, question: next }));
  };
  const previewQuestion = preview.question;

  const dismissBanner = () => setJustCreated(false);

  // Leaves once the queued writes are done, so the summary and the next page see the saved test.
  const leavingRef = useRef(false);
  const goBack = async (withSummary) => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    const failedAdds = failedAddsRef.current;
    await flush();
    // Let the last write's state update render, so the totals are current.
    await new Promise((resolve) => setTimeout(resolve, 0));
    // Left meanwhile (e.g. through the sidebar): the user is not pulled back.
    if (!mountedRef.current) return;
    // An add still running when Done was pressed failed: stay. Its questions are selected
    // again (the bank keeps them), and Done now asks whether to add them or leave without.
    if (failedAddsRef.current !== failedAdds) {
      leavingRef.current = false;
      return;
    }
    if (withSummary && test) {
      const { count, points } = totalsRef.current;
      toast.success(t("test_bank.builder.done_toast", { name: test.name || "", count, points }));
    }
    navigate(backTo);
  };
  const requestLeave = (e, withSummary = false) => {
    e?.preventDefault();
    if (selection.length) setLeave({ open: true, adding: false, withSummary });
    else goBack(withSummary);
  };
  const addAndLeave = async () => {
    // Questions being added leave the selection: the dialog keeps the count it is adding.
    setLeave((l) => ({ ...l, adding: true, count: selection.length }));
    const added = await add(selection);
    if (!added) {
      // Stay: the selection is still there to retry or leave without it (without those the test refused).
      setLeave((l) => ({ ...l, adding: false, count: undefined }));
      return;
    }
    setLeave((l) => ({ ...l, open: false, adding: false }));
    goBack(leave.withSummary);
  };

  const summary = useMemo(() => t("test_bank.builder.summary", { count: totals.count, points: totals.points }), [t, totals]);

  if (builder.loading && !test) {
    return (
      <div className={`${cardClass} flex items-center justify-center gap-3 p-10`} role="status">
        <Spinner className="h-6 w-6" />
        <span className="text-sm text-slate-600">{t("test_bank.editor.loading_test")}</span>
      </div>
    );
  }

  if (builder.loadError || !test) {
    return (
      <div className="flex flex-col gap-4">
        <Link to={backTo} className={backLinkClass}>
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
          {backLabel}
        </Link>
        <div className={`${cardClass} flex flex-col items-center gap-3 p-10 text-center`} role="alert">
          <ExclamationTriangleIcon className="h-8 w-8 text-red-400" aria-hidden="true" />
          <p className="text-sm text-slate-700">{getApiErrorMessage(t, builder.loadError, "test_bank.editor.load_error")}</p>
          <button type="button" onClick={() => builder.reload()} className={`${linkButtonClass} max-sm:min-h-11`}>
            {t("test_bank.builder.retry")}
          </button>
        </div>
      </div>
    );
  }

  const tabClass = (active) =>
    `inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl px-3 text-sm font-medium transition duration-200 sm:h-9 ${focusRing} ${
      active ? "bg-white text-blue-700 shadow-sm ring-1 ring-inset ring-blue-600/20" : "text-slate-600 hover:bg-white/70"
    }`;

  const blockedRemoveDialog = (
    <BlockedRemoveDialog
      isOpen={blockedRemoval.open}
      rows={blockedRemoval.rows}
      passage={blockedRemoval.passage}
      onCancel={() => answerBlockedRemoval(false)}
      onConfirm={() => answerBlockedRemoval(true)}
      onClosed={blockedRemovalClosed}
    />
  );
  // Asked from the preview sheet, the dialog is rendered inside it: nested in the sheet's dialog, it gets the focus,
  // Escape and clicks outside it, and the sheet waits under it (a dialog of its own would have the sheet close
  // instead). Should the window become wide meanwhile, there is no sheet any more: it moves out.
  const removalInSheet = !wide && blockedRemoval.inSheet;

  return (
    <div className="flex min-w-0 flex-col gap-4 motion-safe:animate-fade-in xl:h-full">
      <a href={backTo} onClick={(e) => requestLeave(e)} className={backLinkClass}>
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
        {backLabel}
      </a>

      {justCreated && (
        <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3" role="status">
          <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-emerald-900">{t("test_bank.builder.created_title")}</p>
            <p className="text-sm text-emerald-800">{t("test_bank.builder.created_text")}</p>
          </div>
          <button
            type="button"
            onClick={dismissBanner}
            aria-label={t("test_bank.common.dismiss")}
            className={`${iconButtonClass} h-8 w-8 text-emerald-700 enabled:hover:bg-emerald-100 max-sm:-m-1.5 max-sm:h-11 max-sm:w-11`}
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      )}

      <header className={`${cardClass} p-4 sm:p-5`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          {/* The title keeps 18rem at least: where the actions don't fit beside it, they go under it. */}
          <div className="min-w-[min(100%,18rem)] flex-1">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("test_bank.builder.eyebrow")}</p>
            <div className="mt-1 flex items-start gap-1.5">
              <h1
                ref={headingRef}
                tabIndex={-1}
                title={test.name || ""}
                className="line-clamp-2 min-w-0 break-words text-xl font-semibold tracking-tight text-slate-900 outline-none sm:text-2xl"
              >
                {test.name || t("test_bank.builder.untitled_test")}
              </h1>
              {/* The guide's "?" right after the title; the margins keep it level with the title's first line. */}
              <HelpButton id="tests" title={t("help.tests.title")} className="-my-2 sm:-my-1.5 md:-my-0.5">
                <TestsGuide passages={passagesAvailable} />
              </HelpButton>
            </div>
            {test.description && <p className="mt-1 line-clamp-2 break-words text-sm text-slate-500">{test.description}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
                  test.calculator_is_allowed ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20" : "bg-slate-100 text-slate-600 ring-slate-500/20"
                }`}
              >
                <CalculatorIcon className="h-3.5 w-3.5" aria-hidden="true" />
                {test.calculator_is_allowed ? t("test_bank.form.calculator_allowed") : t("test_bank.editor.calculator_not_allowed")}
              </span>
              {test.periodic_table_is_allowed && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                  <BeakerIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("test_bank.form.periodic_table_allowed")}
                </span>
              )}
              {!isBlankHtml(test.additional_information) && (
                <button
                  type="button"
                  onClick={() => setInfoOpen(true)}
                  className={`inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20 transition duration-200 hover:bg-blue-100 ${focusRing}`}
                >
                  <DocumentTextIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("test_bank.editor.has_additional_information")}
                </button>
              )}
              {/* The page heading the exam's Word prints, when the test has its own (else the subject's title). It opens
                  Edit test on that field, where a heading cut here (phones) reads whole; 44px to tap on phones. */}
              {test.heading && (
                <button
                  type="button"
                  onClick={() => openEdit("heading")}
                  title={test.heading}
                  className={`relative inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-500/20 transition duration-200 hover:bg-slate-200 hover:text-slate-800 max-sm:before:absolute max-sm:before:inset-x-0 max-sm:before:-inset-y-2.5 ${focusRing}`}
                >
                  <PrinterIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span className="truncate">{t("test_bank.editor.heading_chip", { heading: test.heading })}</span>
                </button>
              )}
            </div>
          </div>
          <div className="flex w-full flex-col gap-2 sm:ml-auto sm:w-auto sm:items-end">
            <p className="text-sm font-medium tabular-nums text-slate-700 xl:hidden">{summary}</p>
            <div className="xl:hidden">
              <SaveStatus state={builder.saveState} onRetry={builder.refresh} />
            </div>
            {/* One main button, Done; Edit test and Preview quiet beside it, in "⋯" on phones. */}
            <div className="flex w-full items-center gap-2 sm:w-auto">
              {roomyHeader && (
                <>
                  <button type="button" onClick={() => openEdit()} className={quietButtonClass}>
                    <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
                    {t("test_bank.list.edit_test")}
                  </button>
                  <button type="button" onClick={() => setPreviewOpen(true)} className={quietButtonClass}>
                    <EyeIcon className="h-4 w-4" aria-hidden="true" />
                    {t("test_bank.preview.open")}
                  </button>
                </>
              )}
              <button type="button" onClick={(e) => requestLeave(e, true)} className={doneButtonClass}>
                <CheckIcon className="h-4 w-4" aria-hidden="true" />
                {t("test_bank.builder.done")}
              </button>
              {!roomyHeader && (
                <OverflowMenu
                  label={t("test_bank.common.test_actions")}
                  testId="test-actions"
                  items={[
                    { key: "preview", label: t("test_bank.preview.open"), Icon: EyeIcon, onClick: () => setPreviewOpen(true) },
                    { key: "edit", label: t("test_bank.list.edit_test"), Icon: PencilSquareIcon, onClick: () => openEdit() },
                  ]}
                />
              )}
            </div>
          </div>
        </div>
      </header>

      <div role="tablist" aria-label={t("test_bank.builder.panes_label")} className="sticky top-0 z-10 -mx-4 flex gap-1 bg-slate-100/95 px-4 py-2 backdrop-blur lg:-mx-6 lg:px-6 xl:hidden">
        <button type="button" role="tab" aria-selected={pane === "bank"} onClick={() => setPane("bank")} className={tabClass(pane === "bank")}>
          {t("test_bank.builder.tab_bank")}
        </button>
        <button type="button" role="tab" aria-selected={pane === "test"} onClick={() => setPane("test")} className={tabClass(pane === "test")}>
          {t("test_bank.builder.tab_test")}
          {/* An empty test's count steps back. */}
          <span
            className={`rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums ${
              totals.count ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-600"
            }`}
          >
            {totals.count}
          </span>
        </button>
      </div>

      <div
        className={`grid min-h-0 flex-1 grid-cols-1 gap-4 xl:min-h-[34rem] xl:grid-cols-[minmax(0,1fr)_26rem] 2xl:grid-cols-[minmax(0,1fr)_28rem] ${
          wide ? "min-[1920px]:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_30rem]" : ""
        }`}
      >
        <div className={`min-h-0 ${pane !== "bank" ? "max-xl:hidden" : ""}`}>
          <QuestionBrowser
            testUuid={uuid}
            inTest={inTest}
            pendingIds={pendingIds}
            blockedIds={blockedIds}
            refreshKey={bankKey}
            saving={builder.busy}
            activeUuid={previewQuestion?.uuid}
            wide={wide}
            onOpen={openFromBank}
            onAdd={add}
            onRemove={removeQuestion}
            onSelectionChange={setSelection}
            onLoaded={bankLoaded}
          />
        </div>
        {wide && (
          <div className="min-h-0">
            <QuestionPreview
              layout="panel"
              open
              question={previewQuestion}
              inTest={!!previewQuestion && inTest.has(previewQuestion.uuid)}
              pending={!!previewQuestion && pendingIds.has(previewQuestion.uuid)}
              inTestIds={inTest}
              blockedIds={blockedIds}
              onAdd={() => add([previewQuestion])}
              onAddQuestions={add}
              onRemove={() => removeQuestion(previewQuestion)}
              onPrev={previewIndex > 0 ? () => stepPreview(-1) : null}
              onNext={previewIndex >= 0 && previewIndex < preview.list.length - 1 ? () => stepPreview(1) : null}
            />
          </div>
        )}
        <div className={`min-h-0 ${pane !== "test" ? "max-xl:hidden" : ""}`}>
          <TestComposition
            items={items}
            totals={totals}
            saveState={builder.saveState}
            onRetry={builder.refresh}
            defaultPoints={defaultPoints}
            onDefaultPointsChange={setDefaultPoints}
            activeUuid={previewQuestion?.uuid}
            flashIds={flashIds}
            onOpen={openFromTest}
            onSetPoints={setPoints}
            onRemove={removeItem}
            onMove={move}
            onMovePassage={movePassage}
            onRemovePassage={removePassage}
            onViewPassage={viewPassage}
            onEditPassage={editPassage}
            onAddPassage={passagesAvailable ? () => setContextOpen(true) : undefined}
          />
        </div>
      </div>

      {!wide && (
        <QuestionPreview
          layout="sheet"
          open={preview.open}
          question={previewQuestion}
          inTest={!!previewQuestion && inTest.has(previewQuestion.uuid)}
          pending={!!previewQuestion && pendingIds.has(previewQuestion.uuid)}
          inTestIds={inTest}
          blockedIds={blockedIds}
          onAdd={() => add([previewQuestion])}
          onAddQuestions={add}
          onRemove={() => removeQuestion(previewQuestion)}
          onPrev={previewIndex > 0 ? () => stepPreview(-1) : null}
          onNext={previewIndex >= 0 && previewIndex < preview.list.length - 1 ? () => stepPreview(1) : null}
          onClose={() => setPreview((p) => ({ ...p, open: false }))}
        >
          {removalInSheet && blockedRemoveDialog}
        </QuestionPreview>
      )}

      <EditTestDialog
        isOpen={editOpen}
        test={test}
        focusField={editField}
        onClose={() => setEditOpen(false)}
        onSave={builder.updateTest}
        onUploadFile={builder.uploadInfoFile}
      />
      <InfoDialog
        isOpen={infoOpen}
        html={test.additional_information}
        onClose={() => {
          editAfterInfo.current = false;
          setInfoOpen(false);
        }}
        onEdit={() => {
          editAfterInfo.current = true;
          setInfoOpen(false);
        }}
        onClosed={() => {
          if (!editAfterInfo.current) return;
          editAfterInfo.current = false;
          openEdit();
        }}
      />
      <InfoDialog
        isOpen={passageView.open}
        title={t("passages.panel.title", { name: passageView.passage?.name || "" })}
        onClose={() => setPassageView((view) => ({ ...view, open: false }))}
        onEdit={() => {
          setPassageView((view) => ({ ...view, open: false }));
          editPassage(passageView.passage);
        }}
      >
        <PassageText uuid={passageView.passage?.uuid} />
      </InfoDialog>
      <PassageDialog
        isOpen={passageEdit.open}
        passageUuid={passageEdit.uuid}
        onClose={() => setPassageEdit((edit) => ({ ...edit, open: false }))}
        onSaved={syncPassage}
        onDeleted={(passageUuid) => syncPassage({ uuid: passageUuid, questions: [] })}
      />
      <ContextQuestionDialog isOpen={contextOpen} onClose={() => setContextOpen(false)} inTest={inTest} subject={test.subject || null} onDone={addPassage} />
      <TestPreview open={previewOpen} testUuid={test.uuid} prepare={flush} onClose={() => setPreviewOpen(false)} />
      {!removalInSheet && blockedRemoveDialog}
      <LeaveDialog
        isOpen={leave.open}
        count={leave.count ?? selection.length}
        adding={leave.adding}
        onStay={() => setLeave({ open: false, adding: false })}
        onLeave={() => {
          setLeave({ open: false, adding: false });
          goBack(leave.withSummary);
        }}
        onAddAndLeave={addAndLeave}
      />
      <div aria-live="polite" className="sr-only">
        {announcement}
      </div>
    </div>
  );
}
